import { InventoryDevice } from '@shure/cloud/shared/models/devices';
import { mapDurationFromSysApi } from '@shure/shared/angular/data-access/system-api/mappers';

import { DeviceStatusService } from '../../api/device-status.service';
import { InventoryDeviceFragment } from '../graphql/generated/cloud-sys-api';

import {
	AllAuracastAudioChannelFeatureFragments,
	AllAuracastDeviceFeatureFragments,
	mapAuracastFromSysApi
} from './map-auracast';
import { mapBatteryStateFromSysApi } from './map-battery-state';
import { mapCloudGatewayFromSysApi } from './map-cloud-gateway';
import { mapDeviceAuthenticationFromSysApi } from './map-device-authentication';
import { mapExtendedWarrantyFromSysApi } from './map-extended-warranty';
import { mapFirmwarePackagesFromSysApi } from './map-firmware-packages';
import {
	mapSysApiFirmwareUpdateRequestToFeature,
	mapSysApiUpdateProgressToFeature
} from './map-firmware-update-requests';
import { mapLicenseV3FromSysApi } from './map-license-v3';
import { mapScheduledJobRequestFromSysApi } from './map-scheduled-job-request';
import {
	deviceFragmentToMicStateFeature,
	mapCompatibilityLevelFromSysApi,
	mapDeviceModelFromSysApi,
	mapFeatureValueFromSysApi,
	mapHostedFirmwareVersionFromSysApi
} from './utils';

export function mapInventoryDeviceFromSysApi(
	device: InventoryDeviceFragment,
	statusService: DeviceStatusService,
	auracastChannel: AllAuracastAudioChannelFeatureFragments | null
): InventoryDevice {
	const name = mapFeatureValueFromSysApi(device.features.name?.name, '');
	const mute = {
		muted: device.features.audioMute?.muted ?? false,
		isMissing: !device.description.features.audioMute.supported
	};

	const auracastDevice: AllAuracastDeviceFeatureFragments | null = device.features.auracast
		? {
				auracast: device.features.auracast,
				bluetoothPower: device.features.bluetoothPower
			}
		: null;
	const auracast = device.description.features.auracast?.supported
		? {
				...mapAuracastFromSysApi(auracastDevice, auracastChannel),
				isMissing: !device.features.auracast
			}
		: undefined;

	const batteryLevel = {
		percentage: device.features.batteryLevel?.percentage ?? 0,
		currentState: mapBatteryStateFromSysApi(device.features.batteryLevel?.status.currentState),
		targetState: mapBatteryStateFromSysApi(device.features.batteryLevel?.status.targetState),
		timeToTargetState: mapDurationFromSysApi(device.features.batteryLevel?.status.timeToTargetState),
		isMissing: !device.description.features.batteryLevel?.supported
	};

	const controlNetwork = {
		ipAddress: device.features.controlNetwork
			? mapFeatureValueFromSysApi(device.features.controlNetwork.interface.ipAddress)
			: mapFeatureValueFromSysApi(device.protocol.dmp?.ipAddress),
		macAddress: mapFeatureValueFromSysApi(device.features.controlNetwork?.interface.macAddress),
		isMissing: !device.description.features.controlNetwork.supported
	};

	const firmware = {
		version: mapFeatureValueFromSysApi(device.features.firmware?.version),
		valid: device.features.firmware?.valid ?? true,
		isUpToDate: true,
		isMissing: !device.description.features.firmware.supported
	};

	// the FW Update V2 feature
	const primaryFirmwareUpdateRequestFeature = mapSysApiFirmwareUpdateRequestToFeature(
		device.description.features.firmwareUpdateRequests?.supported,
		device.features.firmwareUpdateRequests?.primary?.current
	);
	const hostedFirmwareUpdateRequestFeature = mapSysApiFirmwareUpdateRequestToFeature(
		device.description.features.firmwareUpdateRequests?.supported,
		device.features.firmwareUpdateRequests?.hosted.current
	);

	// v1 Firmware Update Progress (implicit primary, and explicit hosted) Feature mapped to V2
	const canonicalPrimaryFirmwareUpdateV1Feature = mapSysApiUpdateProgressToFeature(
		device.description.features.updateProgress?.supported,
		device.features.updateProgress
	);
	const canonicalHostedFirmwareUpdateV1Feature = mapSysApiUpdateProgressToFeature(
		device.description.features.hostedUpdateProgress?.supported,
		device.features.hostedUpdateProgress
	);

	const hostedFirmware = {
		key: mapFeatureValueFromSysApi(device.features.hostedFirmware?.key, ''),
		version: mapHostedFirmwareVersionFromSysApi(device.features.hostedFirmware?.version),
		isMissing: !device.description.features.hostedFirmware?.supported
	};

	const identify = {
		identifying: device.features.identify?.identifying || false,
		isMissing: !device.description.features.identify.supported
	};

	const licenseV3 = device.description.features.licenseV3.supported
		? {
				license: mapLicenseV3FromSysApi(device.features.licenseV3),
				isMissing: !device.features.licenseV3
			}
		: undefined;

	const micStatus = deviceFragmentToMicStateFeature(device);

	const serialNumber = {
		serialNumber: mapFeatureValueFromSysApi(device.features.serialNumber?.serialNumber),
		isMissing: !device.description.features.serialNumber.supported
	};

	const availablePackages = {
		...mapFirmwarePackagesFromSysApi(device.features.availablePackages),
		isMissing: !device.description.features.availablePackages.supported
	};

	const proxiedDevices = {
		proxiedDevices: (device.features.proxiedDevices?.proxiedDevices ?? []).map((proxiedDevice) => proxiedDevice),
		isMissing: !device.description.features.proxiedDevices?.supported
	};

	const room = {
		room: {
			name: mapFeatureValueFromSysApi(device.features.room?.room?.name)
		},
		isMissing: !device.description.features.room.supported
	};

	const tags = {
		tags: device.features.tags?.tags ?? [],
		isMissing: !device.description.features.tags?.supported
	};

	const authentication = {
		...mapDeviceAuthenticationFromSysApi(device.features.authentication),
		isMissing: !device.description.features.authentication?.supported
	};

	const cloudGateway = {
		...mapCloudGatewayFromSysApi(device.features.cloudGateway),
		isMissing: !device.description.features.cloudGateway?.supported
	};

	const extendedWarranty = device.description.features.extendedWarranty?.supported
		? {
				...mapExtendedWarrantyFromSysApi(device.features.extendedWarranty),
				isMissing: !device.features.extendedWarranty
			}
		: undefined;

	const firmwareJobRequests = device.features.scheduledJobs?.firmwareJobRequests;
	const scheduledJobs = device.description.features.scheduledJobs?.supported
		? {
				firmwareJobRequests: {
					pendingPrimary: mapScheduledJobRequestFromSysApi(firmwareJobRequests?.pending?.primary),
					pendingHosted: mapScheduledJobRequestFromSysApi(firmwareJobRequests?.pending?.hosted),
					executingPrimary: mapScheduledJobRequestFromSysApi(firmwareJobRequests?.executing?.primary),
					executingHosted: mapScheduledJobRequestFromSysApi(firmwareJobRequests?.executing?.hosted)
				},
				isMissing: !device.features.scheduledJobs
			}
		: undefined;

	const iotRoomProxy = device.description.features.iotRoomProxy?.supported
		? {
				enabled: device.features.iotRoomProxy?.enabled ?? false,
				isMissing: !device.features.iotRoomProxy
			}
		: undefined;

	const unaugmentedDevice: Omit<InventoryDevice, 'conditions'> = {
		id: device.id,
		hardwareId: device.hardwareId,
		name,
		model: mapDeviceModelFromSysApi(device.interface.model ?? ''),
		virtual: device.virtual,
		status: device.status,
		compatibility: mapCompatibilityLevelFromSysApi(device.compatibility),
		manufacturer: device.manufacturer,
		features: {
			availablePackages,
			auracast,
			authentication,
			batteryLevel,
			cloudGateway,
			controlNetwork,
			extendedWarranty,
			firmware,
			primaryFirmwareUpdateRequest:
				primaryFirmwareUpdateRequestFeature ?? canonicalPrimaryFirmwareUpdateV1Feature,
			hostedFirmwareUpdateRequest: hostedFirmwareUpdateRequestFeature ?? canonicalHostedFirmwareUpdateV1Feature,
			hostedFirmware,
			identify,
			iotRoomProxy,
			licenseV3,
			micStatus,
			mute,
			proxiedDevices,
			room,
			scheduledJobs,
			serialNumber,
			tags
		}
	};

	return {
		...unaugmentedDevice,
		conditions: statusService.conditionsFor(<InventoryDevice>unaugmentedDevice)
	};
}
